import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager, LookupContextMapping, LookupRelatedDataFetch } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableFilters } from 'src/app/services/models/dynamic-table.models';
import { DataTableSchema } from 'src/app/services/models/datasets.models';
import { Subject, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LookupDialogComponent, LookupDialogData, LookupDialogResult } from '../input-field-cell/lookup-dialog/lookup-dialog.component';

@Component({
  selector: 'bhub-lookup-cell',
  standalone: false,
  templateUrl: './lookup-cell.component.html',
  styleUrl: './lookup-cell.component.scss'
})
export class LookupCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();

  // UI Properties
  buttonLabel: string = '';

  // Configuration properties
  lookupDatasetId: number | null = null;
  lookupDisplayColumns: string[] = [];
  lookupFilterColumns: string[] = [];
  lookupContextMappings: LookupContextMapping[] = [];
  lookupStoreSelectedRow: boolean = false;
  lookupSelectedRowContextVariable: string = '';

  // State
  private destroy$ = new Subject<void>();
  // Dataset schemas for the lookup dataset — loaded lazily to build fieldReferences
  // when storing the selected row in context (mirrors Filter List's primary-context shape).
  private datasetSchemas: DataTableSchema[] = [];
  private loadedSchemasForDatasetId: number | null = null;

  constructor(
    private dialog: MatDialog,
    private dynamicTableService: DynamicTableService,
    private datasetService: DatasetService
  ) {
    super();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected initializeCell(): void {
    this.updateFieldConfiguration();
  }

  protected onCellOrDataChange(): void {
    this.updateFieldConfiguration();
  }

  protected onCellUpdate(): void {
    this.updateFieldConfiguration();
  }

  protected onDataSet(data: any): void {
    // No-op - this cell doesn't display data
  }

  reloadData(): void {
    this.updateFieldConfiguration();
  }

  private updateFieldConfiguration(): void {
    if (!this.cell) return;

    // Get basic configuration
    this.buttonLabel = this.getConfigValue('lookupButtonLabel', '');

    // Get lookup configuration
    this.lookupDatasetId = this.getConfigValue('lookupDatasetId', null);
    this.lookupDisplayColumns = this.getConfigValue('lookupDisplayColumns', []);
    this.lookupFilterColumns = this.getConfigValue('lookupFilterColumns', []);
    this.lookupContextMappings = this.getConfigValue('lookupContextMappings', []);
    this.lookupStoreSelectedRow = this.getConfigValue('lookupStoreSelectedRow', false);
    this.lookupSelectedRowContextVariable = this.getConfigValue('lookupSelectedRowContextVariable', '');

    // Only fetch dataset schemas when primary-context storage is enabled — otherwise
    // there's no consumer for them. Reuses cached schemas for the same dataset.
    if (this.lookupStoreSelectedRow && this.lookupDatasetId && this.loadedSchemasForDatasetId !== this.lookupDatasetId) {
      this.loadDatasetSchemas(this.lookupDatasetId);
    }
  }

  private loadDatasetSchemas(datasetId: number): void {
    this.datasetService.getDatasetReferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (datasetsMap) => {
          const dataset = datasetsMap.get(datasetId);
          this.datasetSchemas = dataset?.dataTableSchemas || [];
          this.loadedSchemasForDatasetId = datasetId;
        },
        error: () => {
          this.datasetSchemas = [];
          this.loadedSchemasForDatasetId = null;
        }
      });
  }

  private buildFieldReferences(): { [fieldName: string]: { referenceDataId?: number, referenceFieldId?: number } } {
    const fieldReferences: { [fieldName: string]: { referenceDataId?: number, referenceFieldId?: number } } = {};
    this.datasetSchemas.forEach(schema => {
      if (schema.referenceDataId || schema.referenceFieldId) {
        fieldReferences[schema.fieldName] = {
          referenceDataId: schema.referenceDataId || undefined,
          referenceFieldId: schema.referenceFieldId || undefined
        };
      }
    });
    return fieldReferences;
  }

  openLookupDialog(): void {
    if (!this.lookupDatasetId) {
      console.error('Lookup dataset not configured');
      return;
    }

    // For the dialog, we need a display field for selection confirmation
    // Use the first display column if available
    const displayField = this.lookupDisplayColumns.length > 0 ? this.lookupDisplayColumns[0] : '';

    const dialogData: LookupDialogData = {
      datasetId: this.lookupDatasetId,
      displayColumns: this.lookupDisplayColumns,
      filterColumns: this.lookupFilterColumns,
      valueField: displayField, // Not used for storage, just for dialog
      displayField: displayField,
      cellFilters: this.getConfigValue('lookupCellFilters', []),
      viewManager: this.viewManager || undefined,
      title: this.buttonLabel ? `Select ${this.buttonLabel}` : 'Select an Item',
      allowCreate: this.getConfigValue('lookupAllowCreate', false),
      presetFilterFields: this.getConfigValue('lookupPresetFilterFields', []),
      distinct: this.getConfigValue('distinct', false),
      filterDisplayMode: this.getConfigValue('lookupFilterDisplayMode', 'dialog'),
      inlineFilterMatchMode: this.getConfigValue('lookupInlineFilterMatchMode', '')
    };

    const dialogRef = this.dialog.open(LookupDialogComponent, {
      data: dialogData,
      panelClass: 'lookup-dialog-container',
      disableClose: false,
      autoFocus: false
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: LookupDialogResult | undefined) => {
        if (result) {
          this.handleLookupSelection(result);
        }
      });
  }

  private handleLookupSelection(result: LookupDialogResult): void {
    if (!this.viewManager) return;

    const hasMappings = this.lookupContextMappings && this.lookupContextMappings.length > 0;
    const hasPrimaryStore = this.lookupStoreSelectedRow && !!this.lookupSelectedRowContextVariable;
    if (!hasMappings && !hasPrimaryStore) return;

    if (!this.viewManager.contextData) {
      this.viewManager.contextData = {};
    }

    const batchedContextChanges: {[contextKey: string]: any} = {};

    // Primary-context storage: drop the entire selected row into a single context variable,
    // shape-compatible with Filter List's primaryContextVariable convention so downstream
    // cells (context_lookup operands, action saves, etc.) treat both sources the same way.
    if (hasPrimaryStore) {
      const primaryRow = {
        ...result.selectedRow,
        __metadata: {
          datasetId: this.lookupDatasetId ?? undefined,
          fieldReferences: this.buildFieldReferences(),
          isPrimary: true
        }
      };
      this.viewManager.contextData[this.lookupSelectedRowContextVariable] = primaryRow;
      batchedContextChanges[this.lookupSelectedRowContextVariable] = primaryRow;
    }

    // Split mappings into direct (no fetch needed) and related (need async fetch)
    const directMappings = (this.lookupContextMappings || []).filter(m => !m.relatedDataFetch);
    const relatedMappings = (this.lookupContextMappings || []).filter(m => m.relatedDataFetch);

    // Apply direct mappings immediately
    directMappings.forEach(mapping => {
      const contextObject = this.applyFieldMappings(mapping, result.selectedRow);
      batchedContextChanges[mapping.contextVariable] = contextObject;
    });

    if (relatedMappings.length === 0) {
      // No related fetches needed — emit now
      if (Object.keys(batchedContextChanges).length > 0) {
        this.contextDataChange.emit(batchedContextChanges);
      }
      return;
    }

    // Fetch related data for each mapping that needs it
    const fetches$ = relatedMappings.map(mapping => {
      const fetch = mapping.relatedDataFetch!;
      const filterValue = result.selectedRow[fetch.sourceField];

      if (filterValue === null || filterValue === undefined) {
        return of({ mapping, rows: [] as any[] });
      }

      const filters: DynamicTableFilters = {
        [fetch.filterFieldId]: {
          values: [filterValue],
          matchMode: 'exact'
        }
      };

      // For 'first', no sorting needed. For lowest/highest, sort by the selection field.
      // For 'match', fetch all and filter client-side.
      let orderBy: { fieldId: number; direction: 'asc' | 'desc' }[] | undefined;
      let limit = fetch.limit || 1;

      if (fetch.selectionMode === 'lowest' && fetch.selectionFieldId) {
        orderBy = [{ fieldId: fetch.selectionFieldId, direction: 'asc' }];
      } else if (fetch.selectionMode === 'highest' && fetch.selectionFieldId) {
        orderBy = [{ fieldId: fetch.selectionFieldId, direction: 'desc' }];
      } else if (fetch.selectionMode === 'match') {
        // Need all results to find the matching one client-side
        limit = -1;
      }

      return this.dynamicTableService.selectEntries(
        fetch.datasetId,
        limit,
        undefined,
        filters,
        false,
        orderBy
      ).pipe(
        map(response => ({ mapping, rows: response.rowData || [] })),
        catchError(() => of({ mapping, rows: [] as any[] }))
      );
    });

    forkJoin(fetches$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(results => {
        results.forEach(({ mapping, rows }) => {
          if (rows.length === 0) return;

          const fetch = mapping.relatedDataFetch!;
          let selectedRow = rows[0]; // Default: first row (covers 'first', 'lowest', 'highest')

          if (fetch.selectionMode === 'match' && fetch.selectionFieldName && fetch.matchValue) {
            // Find the row where the selection field matches the configured value
            const matchLower = fetch.matchValue.toLowerCase();
            const matched = rows.find(row => {
              const fieldValue = row[fetch.selectionFieldName!];
              return fieldValue != null && String(fieldValue).toLowerCase() === matchLower;
            });
            if (matched) {
              selectedRow = matched;
            } else {
              return; // No matching row found
            }
          }

          const contextObject = this.applyFieldMappings(mapping, selectedRow);
          batchedContextChanges[mapping.contextVariable] = contextObject;
        });

        if (Object.keys(batchedContextChanges).length > 0) {
          this.contextDataChange.emit(batchedContextChanges);
        }
      });
  }

  private applyFieldMappings(mapping: LookupContextMapping, sourceRow: any): any {
    if (!mapping.contextVariable || !this.viewManager) return {};

    let contextObject = this.viewManager.contextData[mapping.contextVariable];
    if (!contextObject || typeof contextObject !== 'object') {
      contextObject = {};
    }

    if (mapping.fieldMappings && mapping.fieldMappings.length > 0) {
      mapping.fieldMappings.forEach(fieldMapping => {
        if (fieldMapping.targetField) {
          if (fieldMapping.sourceFields && fieldMapping.sourceFields.length > 0) {
            const separator = fieldMapping.separator ?? ' ';
            const values = fieldMapping.sourceFields
              .map(f => sourceRow[f])
              .filter(v => v != null && v !== '');
            contextObject[fieldMapping.targetField] = values.join(separator);
          } else if (fieldMapping.sourceField) {
            contextObject[fieldMapping.targetField] = sourceRow[fieldMapping.sourceField];
          }
        }
      });
    }

    this.viewManager.contextData[mapping.contextVariable] = contextObject;
    return contextObject;
  }
}
